import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { ConstructionProperty } from 'models/property';
import { formatTeethList } from 'utils/teeth';

type ConstructionSummaryPropertiesProps = {
    properties: ConstructionProperty;
};
const ConstructionSummaryProperties: FC<ConstructionSummaryPropertiesProps> = ({ properties }) => {
    const { t: translate } = useTranslation(['construction-props']);

    return (
        <Box display="flex" flexWrap="wrap">
            {properties?.colorSampling || properties?.color || properties?.shade ? (
                <Box display="flex" mr={3}>
                    <Box mr={0.5}>
                        <Typography variant="subtitle2">{translate('construction-props:SHADE_COLOR')}:</Typography>
                    </Box>
                    <Box>
                        {properties?.colorSampling ? translate('construction-props:COLOR_SAMPLING_AT_LAB') : ''}
                        {properties?.colorSampling && properties?.shade ? ',' : ''}
                        {properties?.shade && properties?.color
                            ? properties?.shade + ', ' + properties?.color
                            : properties?.shade
                              ? properties?.shade
                              : properties?.color
                                ? properties?.color
                                : ''}
                    </Box>
                </Box>
            ) : undefined}

            {properties?.design ? (
                <Box display="flex" mr={3}>
                    <Box mr={0.5}>
                        <Typography variant="subtitle2">{translate('construction-props:DESIGN')}:</Typography>
                    </Box>
                    <Box>{translate(`construction-props:${properties?.design}`)}</Box>
                </Box>
            ) : undefined}

            {properties?.retainment ? (
                <Box display="flex" mr={3}>
                    <Box mr={0.5}>
                        <Typography variant="subtitle2">{translate('construction-props:RETAINMENT')}:</Typography>
                    </Box>
                    <Box>{properties?.retainment}</Box>
                </Box>
            ) : undefined}

            {properties?.ponticDesign ? (
                <Box display="flex" mr={3}>
                    <Box mr={0.5}>
                        <Typography variant="subtitle2">{translate('construction-props:PONTIC_DESIGN')}:</Typography>
                    </Box>
                    <Box>{translate(`construction-props:${properties?.ponticDesign}`)}</Box>
                </Box>
            ) : undefined}

            {properties?.claspTeeth?.length ? (
                <Box display="flex" mr={3}>
                    <Box mr={0.5}>
                        <Typography variant="subtitle2">{translate('construction-props:CLASP_TEETH')}:</Typography>
                    </Box>
                    <Box>{formatTeethList(properties.claspTeeth.map((claspTooth) => claspTooth.id))}</Box>
                </Box>
            ) : undefined}
            {properties?.claspMaterial ? (
                <Box display="flex" mr={3}>
                    <Box mr={0.5}>
                        <Typography variant="subtitle2">{translate('construction-props:CLASP_MATERIAL')}:</Typography>
                    </Box>
                    <Box>{translate(`construction-props:${properties?.claspMaterial}`)}</Box>
                </Box>
            ) : undefined}
            {properties?.orthColor ? (
                <Box display="flex" mr={3}>
                    <Box mr={0.5}>
                        <Typography variant="subtitle2">
                            {translate('construction-props:ORTHODONTICS_COLORS')}:
                        </Typography>
                    </Box>
                    <Box>{properties?.orthColor}</Box>
                </Box>
            ) : undefined}
            {properties?.facialLoop ? (
                <Box display="flex" mr={3}>
                    <Box mr={0.5}>
                        <Typography variant="subtitle2">{translate('construction-props:FACIAL_LOOP')}:</Typography>
                    </Box>
                    <Box>{translate(`construction-props:${properties?.facialLoop}`)}</Box>
                </Box>
            ) : undefined}
            {properties?.claspType ? (
                <Box display="flex" mr={3}>
                    <Box mr={0.5}>
                        <Typography variant="subtitle2">{translate('construction-props:CLASP_TYPES')}:</Typography>
                    </Box>
                    <Box>{translate(`construction-props:${properties?.claspType}`)}</Box>
                </Box>
            ) : undefined}
            {properties?.springType ? (
                <Box display="flex" mr={3}>
                    <Box mr={0.5}>
                        <Typography variant="subtitle2">{translate('construction-props:SPRING_TYPES')}:</Typography>
                    </Box>
                    <Box>{translate(`construction-props:${properties?.springType}`)}</Box>
                </Box>
            ) : undefined}
            {properties?.springTeeth?.length ? (
                <Box display="flex" mr={3}>
                    <Box mr={0.5}>
                        <Typography variant="subtitle2">{translate('construction-props:SPRING_TEETH')}:</Typography>
                    </Box>
                    <Box>{formatTeethList(properties.springTeeth.map((springTooth) => springTooth.id))}</Box>
                </Box>
            ) : undefined}
            {properties?.expansionScrew ? (
                <Box display="flex" mr={3}>
                    <Box mr={0.5}>
                        <Typography variant="subtitle2">{translate('construction-props:EXPANSION_SCREW')}:</Typography>
                    </Box>
                    <Box>{translate('common:YES')}</Box>
                </Box>
            ) : undefined}
            {properties?.protrusion ? (
                <Box display="flex" mr={3}>
                    <Box mr={0.5}>
                        <Typography variant="subtitle2">{translate('construction-props:PROTRUSION')}:</Typography>
                    </Box>
                    <Box>{properties?.protrusion}</Box>
                </Box>
            ) : undefined}
            {properties?.occlusion ? (
                <Box display="flex" mr={3}>
                    <Box mr={0.5}>
                        <Typography variant="subtitle2">{translate('construction-props:OCCLUSION')}:</Typography>
                    </Box>
                    <Box>{translate(`construction-props:${properties?.occlusion}`)}</Box>
                </Box>
            ) : undefined}
            {properties?.contactType ? (
                <Box display="flex" mr={3}>
                    <Box mr={0.5}>
                        <Typography variant="subtitle2">{translate('construction-props:CONTACT_TYPE')}:</Typography>
                    </Box>
                    <Box>{translate(`construction-props:${properties?.contactType}`)}</Box>
                </Box>
            ) : undefined}
            {properties?.implantSteps ? (
                <Box display="flex" mr={3}>
                    <Box mr={0.5}>
                        <Typography variant="subtitle2">{translate('construction-props:IMPLANT_STEPS')}:</Typography>
                    </Box>
                    <Box>{translate(`construction-props:${properties?.implantSteps}`)}</Box>
                </Box>
            ) : undefined}
            {properties?.dentureSteps ? (
                <Box display="flex" mr={3}>
                    <Box mr={0.5}>
                        <Typography variant="subtitle2">{translate('construction-props:DENTURE_STEPS')}:</Typography>
                    </Box>
                    <Box>{translate(`construction-props:${properties?.dentureSteps}`)}</Box>
                </Box>
            ) : undefined}
            {properties?.testingSteps ? (
                <Box display="flex" mr={3}>
                    <Box mr={0.5}>
                        <Typography variant="subtitle2">{translate('construction-props:TESTING_STEPS')}:</Typography>
                    </Box>
                    <Box>{translate(`construction-props:${properties?.testingSteps}`)}</Box>
                </Box>
            ) : undefined}
        </Box>
    );
};

export default ConstructionSummaryProperties;
